import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';

export type MailVisualState =
  | 'unavailable'
  | 'ready'
  | 'sent_pending'
  | 'in_transit'
  | 'delivered'
  | 'failed';

export interface MailIconLayer {
  prefix: IconPrefix;
  icon: IconName;
  transform?: string;
  cssClass?: string;
}

export interface MailIconConfig {
  variant: 'simple' | 'layers';
  prefix?: IconPrefix;
  icon?: IconName;
  layers?: MailIconLayer[];
}

export interface MailVisualStateInput {
  enabled: boolean;
  mailStatus?: string | null;
  sentAt?: string | boolean | null;
}

export const MAIL_LEGEND_COLORS: Record<MailVisualState, string> = {
  unavailable: 'medium',
  ready: 'secondary',
  sent_pending: 'primary',
  in_transit: 'warning',
  delivered: 'primary',
  failed: 'danger',
};

export const MAIL_LEGEND_LABELS: Record<MailVisualState, string> = {
  unavailable: 'Niet beschikbaar',
  ready: 'Beschikbaar, maar nog niet verstuurd',
  sent_pending: 'Verstuurd — wacht op bevestiging van Mailgun',
  in_transit: 'Onderweg bij Mailgun',
  delivered: 'Afgeleverd',
  failed: 'Mislukt of gebounced',
};

const FAILED_STATUSES = new Set([
  'failed',
  'bounced',
  'temporary_fail',
  'permanent_fail',
]);

const MAIL_VISUAL_STATES: MailVisualState[] = [
  'unavailable',
  'ready',
  'sent_pending',
  'in_transit',
  'delivered',
  'failed',
];

export function resolveMailVisualState(
  input: MailVisualStateInput,
): MailVisualState {
  if (!input.enabled) {
    return 'unavailable';
  }

  const status = input.mailStatus;

  if (status) {
    if (FAILED_STATUSES.has(status)) {
      return 'failed';
    }

    if (status === 'delivered') {
      return 'delivered';
    }

    if (status === 'sent' || status === 'accepted') {
      return 'in_transit';
    }
  }

  if (input.sentAt && !status) {
    return 'sent_pending';
  }

  return 'ready';
}

export function getMailIconConfig(state: MailVisualState): MailIconConfig {
  switch (state) {
    case 'unavailable':
      return simpleIcon('envelope-open', 'fal');
    case 'ready':
      return simpleIcon('envelope', 'fal');
    case 'sent_pending':
      return simpleIcon('envelope', 'fas');
    case 'in_transit':
      return simpleIcon('envelope', 'fas');
    case 'delivered':
      return simpleIcon('envelope-circle-check', 'fal');
    case 'failed':
      return simpleIcon('envelope-dot', 'fal');
    default:
      return simpleIcon('envelope', 'fal');
  }
}

export function getMailLegendItems(): Array<{
  state: MailVisualState;
  color: string;
  label: string;
  config: MailIconConfig;
}> {
  return MAIL_VISUAL_STATES.map((state) => ({
    state,
    color: MAIL_LEGEND_COLORS[state],
    label: MAIL_LEGEND_LABELS[state],
    config: getMailIconConfig(state),
  }));
}

function simpleIcon(icon: IconName, prefix: IconPrefix): MailIconConfig {
  return {
    variant: 'simple',
    prefix,
    icon,
  };
}
